if (!customElements.get('infinite-scroll')) {
  // Coming back from a product page, the browser only re-renders page 1 of the collection.
  // We remember how many extra pages were loaded and which product was tapped, then reload
  // those pages and scroll back to that product.
  // Keyed by the current URL, which filters/sorting change without a page load
  const storageKey = () => 'infinite-scroll:' + window.location.pathname + window.location.search;
  const MAX_AGE_MS = 30 * 60 * 1000;
  // Start fetching the next page this far before the end, so a quick flick on a phone
  // doesn't overtake it
  const PRELOAD_PX = 1500;

  const navEntry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  const isBackForward = navEntry && navEntry.type === 'back_forward';
  let savedState = null;
  try {
    savedState = JSON.parse(sessionStorage.getItem(storageKey()));
  } catch (e) {}
  // Nothing beyond page 1 loaded: the browser's own scroll restore already works
  if (!isBackForward || !savedState || !savedState.pages || Date.now() - savedState.time > MAX_AGE_MS) {
    savedState = null;
  }
  if (savedState && 'scrollRestoration' in history) history.scrollRestoration = 'manual';
  if (!isBackForward) {
    try {
      sessionStorage.removeItem(storageKey());
    } catch (e) {}
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('#product-grid a[href*="/products/"]');
    if (!link) return;
    const grid = document.getElementById('product-grid');
    try {
      sessionStorage.setItem(
        storageKey(),
        JSON.stringify({
          pages: Number(grid.dataset.pagesLoaded || 0),
          productPath: new URL(link.href, window.location.origin).pathname,
          scrollY: window.scrollY,
          time: Date.now(),
        })
      );
    } catch (e) {}
  });

  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      connectedCallback() {
        this.spinner = this.querySelector('.loading__spinner');
        this.moreLink = this.querySelector('.infinite-scroll__more');
        this.loading = null;

        // Fallback link: clicking it loads the next page in place too
        if (this.moreLink) {
          this.moreLink.addEventListener('click', (event) => {
            event.preventDefault();
            this.loadNext();
          });
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting && !this.restoring) this.loadNext();
          },
          { rootMargin: `0px 0px ${PRELOAD_PX}px 0px` }
        );
        this.observer.observe(this);

        if (savedState) {
          const state = savedState;
          savedState = null; // only once per page view (filters re-render this element)
          this.restore(state);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async restore(state) {
        this.restoring = true;
        for (let i = 0; i < state.pages && this.isConnected && this.dataset.nextUrl; i++) {
          if (!(await this.loadNext({ chain: false }))) break;
        }
        this.restoring = false;

        const card = Array.from(document.querySelectorAll('#product-grid a[href*="/products/"]')).find(
          (a) => new URL(a.href, window.location.origin).pathname === state.productPath
        );
        const item = card && card.closest('.grid__item');
        if (item) {
          const top = item.getBoundingClientRect().top + window.scrollY - window.innerHeight / 4;
          window.scrollTo({ top, behavior: 'instant' });
        } else {
          window.scrollTo({ top: state.scrollY, behavior: 'instant' });
        }
      }

      // Resolves true when a page was appended, false on error or when nothing is left
      loadNext({ chain = true } = {}) {
        const nextUrl = this.dataset.nextUrl;
        if (this.loading) return this.loading;
        if (!nextUrl) return Promise.resolve(false);
        this.classList.remove('is-errored');
        if (this.spinner) this.spinner.classList.remove('hidden');

        // Section Rendering API: fetch just this section's HTML for the next page
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        this.loading = fetch(url.toString())
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const grid = document.getElementById('product-grid');
            const newItems = doc.querySelectorAll('#product-grid > .grid__item');
            // Appended cards skip the reveal animation: it kept them at ~0 opacity for up to
            // 1.2s (staggered delay) and left black gaps when they landed straight on screen.
            newItems.forEach((item) => {
              const node = document.importNode(item, true);
              node.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in');
              node.removeAttribute('data-cascade');
              grid.appendChild(node);
            });
            grid.dataset.pagesLoaded = Number(grid.dataset.pagesLoaded || 0) + 1;

            const next = doc.querySelector('infinite-scroll');
            this.loading = null;
            if (this.spinner) this.spinner.classList.add('hidden');
            if (next && next.dataset.nextUrl) {
              this.dataset.nextUrl = next.dataset.nextUrl;
              if (this.moreLink) this.moreLink.href = next.dataset.nextUrl;
              // Still on screen (short pages / tall screens)? Keep loading.
              const rect = this.getBoundingClientRect();
              if (chain && !this.restoring && rect.top < window.innerHeight + PRELOAD_PX) this.loadNext();
            } else {
              delete this.dataset.nextUrl;
              this.remove(); // last page reached
            }
            return true;
          })
          .catch(() => {
            this.loading = null;
            this.classList.add('is-errored'); // show the "Load more" link to retry
            if (this.spinner) this.spinner.classList.add('hidden');
            return false;
          });
        return this.loading;
      }
    }
  );
}
